import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Heading,
  Input,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Text
} from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormError } from 'components/onboarding/form/FormError';
import { useTokenList } from 'hooks/query/useTokenList';
import { ExtendedRewardProgram, ProgramToken } from 'types';
import { PowerSwitchStatus } from 'api/blockchain/rewardProgram';
import { yupResolver } from '@hookform/resolvers/yup';
import { rescueERC20Schema } from 'components/onboarding/deployment/formSchema';
import { IRescueERC20Form } from 'components/onboarding/deployment/type';
import { ethers } from 'ethers';
import { useWeb3React } from 'hooks/web3';
import { useToast } from '@chakra-ui/react';
import { useTransactions } from 'store/transactions/useTransactions';
import IUniswapV2ERC20 from '@uniswap/v2-core/build/IUniswapV2ERC20.json';
import SelectTokenInput from 'components/onboarding/deployment/selectTokens/selectTokenInput';

type Props = {
  program: ExtendedRewardProgram;
  programState?: PowerSwitchStatus;
  rewardPoolAbi: ethers.ContractInterface;
};

export const RescueERC20Form: FC<Props> = ({
  program,
  programState,
  rewardPoolAbi
}) => {
  const [selectedTokens, setSelectedTokens] = useState<ProgramToken[]>([]);

  const toast = useToast();
  const { provider } = useWeb3React();
  const { data: tokenList = [] } = useTokenList();
  const { rescueERC20 } = useTransactions();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IRescueERC20Form>({ resolver: yupResolver(rescueERC20Schema) });

  const unrescuableTokens = useMemo(() => {
    const rewardTokenAddress =
      program.rewardProgramTokens.rewardToken.address.toLowerCase();
    const bonusTokenAddresses = program.rewardProgramTokens.bonusTokens.map(
      (t) => t.address.toLowerCase()
    );
    return [rewardTokenAddress, ...bonusTokenAddresses];
  }, [program]);

  const filteredTokenList = useMemo(() => {
    return tokenList.filter(
      (token) => !unrescuableTokens.includes(token.address.toLowerCase())
    );
  }, [unrescuableTokens, tokenList]);

  const handleRescueToken = (d: IRescueERC20Form) => {
    if (selectedTokens.length > 0) {
      rescueERC20(
        program.rewardProgramAddress,
        program.poolAddress,
        selectedTokens.map((d) => d.address),
        d.recipient,
        rewardPoolAbi
      );
    }
  };

  const setSelectedToken = async (token: ProgramToken) => {
    const tokenAddress = token.address;
    const tokenContract = new ethers.Contract(
      tokenAddress,
      IUniswapV2ERC20.abi,
      provider
    );

    const balance = await tokenContract.balanceOf(program.poolAddress);

    if (balance.eq(0)) {
      toast({
        title: 'Error',
        description: 'This token has 0 balance in the reward pool',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    } else {
      //@ts-ignore
      setSelectedTokens([...new Set(selectedTokens.concat(token))]);
    }
  };

  const handleRemoveToken = (token: ProgramToken) => {
    setSelectedTokens(
      selectedTokens.filter((t) => t.address !== token.address)
    );
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Rescue Tokens
      </Heading>
      <Text color='gray.200' mb={6}>
        This option is only available when the reward program has been
        permanently shutdown.
      </Text>
      <Text color='gray.200' mb={6}>
        This option allows you to withdraw any token from the reward program
        pool.
      </Text>
      <SimpleGrid columns={[1, 1, 3, 4]} gap={4}>
        <GridItem>
          <SelectTokenInput
            tokenList={filteredTokenList}
            setSelectedToken={setSelectedToken}
            required
          />
        </GridItem>

        <GridItem>
          <FormControl isRequired isInvalid={!!errors.recipient}>
            <FormLabel>Enter Address</FormLabel>
            <Input
              id='recipient'
              type='text'
              borderColor={'gray.300'}
              placeholder='0x'
              {...register('recipient')}
            />
            {errors.recipient && (
              <FormError>{errors.recipient.message}</FormError>
            )}
          </FormControl>
        </GridItem>

        <GridItem>
          <Flex height='100%' alignItems='flex-end'>
            <Button
              onClick={handleSubmit((d) => handleRescueToken(d))}
              borderRadius='lg'
              disabled={programState !== PowerSwitchStatus.SHUTDOWN}
            >
              {programState !== PowerSwitchStatus.SHUTDOWN
                ? 'Not Shutdown'
                : 'Rescue tokens'}
            </Button>
          </Flex>
        </GridItem>
      </SimpleGrid>
      <SimpleGrid columns={[1, 2, 3, 4]} spacing={4} mt={6}>
        {selectedTokens.map((token, index) => (
          <GridItem key={index} colStart={index === 0 ? 1 : 'auto'}>
            <Tag size='lg' width='100%' justifyContent='space-between' py={3}>
              <TagLabel color='white'>
                {token.tokenSymbol.toUpperCase()}
              </TagLabel>
              <TagCloseButton onClick={() => handleRemoveToken(token)} />
            </Tag>
          </GridItem>
        ))}
      </SimpleGrid>
    </>
  );
};
